import React, { useState, useEffect } from 'react'
import TransferList from "../../common/components/widgets/TransferList";
import Button from "@material-ui/core/Button/index";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions/index";
import DialogContent from "@material-ui/core/DialogContent/index";
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import SimpleSearch from '../../common/components/data-table/SimpleSearch';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from "@material-ui/core/TextField";
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useUserGroupTeamAvailableUsers } from '../../hooks/useUserGroupTeamAvailableUsers';
import { useUpdateUserGroupsTeam } from '../../hooks/useUpdateUserGroupsTeam';
import { useCreateUserGroupsTeam } from '../../hooks/useCreateUserGroupsTeam';
import useAppStore from "../../store/appStore";
import Swal from 'sweetalert2';

const CustomButton = withStyles((theme) => ({
  root: {
    width: 115,
    fontSize: 16,
    textTransform: 'none',
    color: '#fff',
    backgroundColor: '#E91E63',
    border: 'none',
    '&:hover': {
      backgroundColor: '#FF4282',
    },
    '&:active': {
      backgroundColor: '#FF4282',
    },
    '&:focus': {
      border: 'none'
    },
  },
}))(Button);

function TeamSettings(props) {
  const open = props.open;
  const userGroupId = props.userGroupId;
  const isCreate = props.isCreate || false;
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [nameSearch, setNameSearch] = useState('');
  const [captainOptions, setCaptainOptions] = useState([]);
  const [selectedCaptain, setSelectedCaptain] = useState(null);
  const [captainError, setCaptainError] = useState('');
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [description, setDescription] = useState('');

  // Zustand store for team users
  const teamUsersLeft = useAppStore((state) => state.teamUsersLeft);
  const teamUsersRight = useAppStore((state) => state.teamUsersRight);
  const setTeamUsersLeft = useAppStore((state) => state.setTeamUsersLeft);
  const setTeamUsersRight = useAppStore((state) => state.setTeamUsersRight);
  const clearTeamUsers = useAppStore((state) => state.clearTeamUsers);

  // Wrapper for handleClose that clears Zustand store
  const handleCloseWithCleanup = () => {
    clearTeamUsers();
    props.handleClose();
  };

  // Fetch available users based on search
  const { data: availableUsersData, previousData, isLoading, error } = useUserGroupTeamAvailableUsers(userGroupId, nameSearch);
  
  // Update team mutation
  const updateTeamMutation = useUpdateUserGroupsTeam();
  
  // Create team mutation
  const createTeamMutation = useCreateUserGroupsTeam();

  // Clear Zustand store and reset local state when dialog opens
  useEffect(() => {
    if (open) {
      clearTeamUsers();
      
      // Reset local states
      setLeft([]);
      setNameSearch('');
      setTitle('');
      setDescription('');
      setTitleError('');
      setCaptainError('');
      
      // Reinitialize right side with team members from props (edit mode)
      if (!isCreate && props.teamData?.members) {
        const membersArray = props.teamData.members.map((member) => ({
          id: member.id,
          value: `${member.name} ${member.surname}`
        }));
        setRight(membersArray);
        setTeamUsersRight(membersArray);
        
        // Initialize captain options
        const options = props.teamData.members.map((member) => ({
          value: member.id,
          label: `${member.name} ${member.surname}`
        }));
        setCaptainOptions(options);
        
        // Set selected captain if exists
        if (props.teamData?.captain) {
          setSelectedCaptain({
            value: props.teamData.captain.id,
            label: `${props.teamData.captain.name} ${props.teamData.captain.surname}`
          });
        } else {
          setSelectedCaptain(null);
        }
      } else {
        // Create mode: empty right and no captain
        setRight([]);
        setCaptainOptions([]);
        setSelectedCaptain(null);
      }
    }
  }, [open, clearTeamUsers, isCreate, props.teamData?.members, props.teamData?.captain, setTeamUsersRight]);

  // Populate right side with team members (only in edit mode)
  // This effect is now merged into the one above, so we can remove it or keep as backup
  useEffect(() => {
    if (!isCreate && props.teamData?.members && open) {
      const membersArray = props.teamData.members.map((member) => ({
        id: member.id,
        value: `${member.name} ${member.surname}`
      }));
      setRight(membersArray);
      // Initialize Zustand store with team members
      setTeamUsersRight(membersArray);
    }
  }, [isCreate, props.teamData?.members, setTeamUsersRight, open]);

  // Populate left side with available users from search + users removed from right
  useEffect(() => {
    if (availableUsersData && availableUsersData !== previousData) {
      const users = availableUsersData.availableUsersForTeam?.records;
      if (users) {
        const usersArray = users.map((user) => ({
          id: user._id,
          value: `${user.sigla || ''} ${user.name} ${user.surname || ''}`
        }));
        
        // Merge search results with users removed from right (in Zustand)
        // Filter out users already in search results to avoid duplicates
        // Also filter out users already in right (team members)
        const removedUsers = teamUsersLeft.filter(
          (removedUser) => 
            !usersArray.some((u) => u.id === removedUser.id) &&
            !right.some((u) => u.id === removedUser.id)
        );
        
        // Also filter search results to exclude users already in right
        const filteredUsersArray = usersArray.filter(
          (user) => !right.some((u) => u.id === user.id)
        );
        
        const mergedUsers = [...filteredUsersArray, ...removedUsers];
        console.log('useEffect updating left', { filteredUsersArray, removedUsers, mergedUsers, right });
        setLeft(mergedUsers);
      }
    }
  }, [availableUsersData, previousData, teamUsersLeft, right]);

  // Initialize captain options and selected captain (only in edit mode)
  useEffect(() => {
    if (!isCreate && props.teamData?.members) {
      const options = props.teamData.members.map((member) => ({
        value: member.id,
        label: `${member.name} ${member.surname}`
      }));
      setCaptainOptions(options);

      if (props.teamData?.captain) {
        setSelectedCaptain({
          value: props.teamData.captain.id,
          label: `${props.teamData.captain.name} ${props.teamData.captain.surname}`
        });
      }
    }
  }, [isCreate, props.teamData?.members, props.teamData?.captain]);

  // Reset all states when opening in create mode
  useEffect(() => {
    if (isCreate && open) {
      setRight([]);
      setLeft([]);
      setSelectedCaptain(null);
      setCaptainOptions([]);
      setTitle('');
      setDescription('');
      setTitleError('');
      setCaptainError('');
      setNameSearch('');
      clearTeamUsers(); // Clear Zustand store
    }
  }, [isCreate, open, clearTeamUsers]);

  // Update captain options based on right side changes
  useEffect(() => {
    // Create options from right side users
    const options = right.map((user) => ({
      value: user.id,
      label: user.value
    }));
    setCaptainOptions(options);

    // Check if current selected captain is still in the list
    if (selectedCaptain) {
      const captainStillExists = right.some(user => user.id === selectedCaptain.value);
      if (!captainStillExists) {
        setSelectedCaptain(null); // Reset captain if removed from team
      }
    }
  }, [right, selectedCaptain]);
  
  function handleChange(newLeft, newRight) {
    console.log('handleChange called', { newLeft, newRight, currentLeft: left, currentRight: right });
    
    // Since TransferList passes old values, we need to recalculate based on what changed
    // Compare with current state to understand what moved
    
    // Remove duplicates from newRight (prevent adding same user multiple times)
    const uniqueNewRight = newRight.filter((user, index, self) => 
      index === self.findIndex((u) => u.id === user.id)
    );
    
    // Find users moved from right to left (removed from team)
    const removedFromRight = right.filter((user) => 
      !uniqueNewRight.some((u) => u.id === user.id)
    );
    
    // Find users moved from left to right (added to team)
    const addedToRight = uniqueNewRight.filter((user) => 
      !right.some((u) => u.id === user.id)
    );
    
    console.log('Changes detected', { removedFromRight, addedToRight });
    
    // Calculate the new left: current left minus what was added to right
    let calculatedNewLeft = left.filter((user) => 
      !addedToRight.some((u) => u.id === user.id)
    );
    
    // Add back users that were removed from right
    removedFromRight.forEach((user) => {
      if (!calculatedNewLeft.some((u) => u.id === user.id)) {
        calculatedNewLeft = [...calculatedNewLeft, user];
      }
    });
    
    // Update Zustand store
    let updatedTeamUsersLeft = [...teamUsersLeft];
    
    // Add users removed from right to Zustand
    removedFromRight.forEach((user) => {
      if (!updatedTeamUsersLeft.some((u) => u.id === user.id)) {
        updatedTeamUsersLeft = [...updatedTeamUsersLeft, user];
      }
    });
    
    // Remove users added to right from Zustand
    addedToRight.forEach((user) => {
      updatedTeamUsersLeft = updatedTeamUsersLeft.filter((u) => u.id !== user.id);
    });
    
    setTeamUsersLeft(updatedTeamUsersLeft);
    
    console.log('Setting new state', { calculatedNewLeft, uniqueNewRight });
    
    // Update local state
    setLeft(calculatedNewLeft);
    setRight(uniqueNewRight);
    
    // Clear captain error when users change
    if (captainError) {
      setCaptainError('');
    }
  }

  function save() {
    // Validate title is not empty
    const currentTitle = title || props.teamData?.title;
    if (!currentTitle || currentTitle.trim() === '') {
      setTitleError('Please enter a team title');
      return;
    }

    // Validate captain is selected
    if (!selectedCaptain) {
      setCaptainError('Please select a captain');
      return;
    }

    // Validate at least one user in team
    if (right.length === 0) {
      setCaptainError('Team must have at least one member');
      return;
    }

    // Prepare data for API
    const teamData = {
      groupId: userGroupId,
      title: currentTitle,
      description: description || props.teamData?.description || '',
      userIds: right.map(user => user.id),
      captainId: selectedCaptain.value
    };

    if (isCreate) {
      // Create new team
      createTeamMutation.mutate(teamData, {
        onSuccess: () => {     
          Swal.fire({
            type: 'success',
            title: 'Team created successfully',
            showConfirmButton: false,
            timer: 1500
          });
          handleCloseWithCleanup();
        },
        onError: (error) => {
          Swal.fire({
            type: 'error',
            title: 'Error creating team',
            text: error.message || 'An error occurred while creating the team',
            showConfirmButton: true
          });
          handleCloseWithCleanup();
        }
      });
    } else {
      // Update existing team
      const teamId = props.teamData?._id;
      updateTeamMutation.mutate({ updateData: teamData, id: teamId }, {
        onSuccess: () => {     
          Swal.fire({
            type: 'success',
            title: 'Team updated successfully',
            showConfirmButton: false,
            timer: 1500
          });
          handleCloseWithCleanup();
        },
        onError: (error) => {
          Swal.fire({
            type: 'error',
            title: 'Error updating team',
            text: error.message || 'An error occurred while updating the team',
            showConfirmButton: true
          });
          handleCloseWithCleanup();
        }
      });
    }
  }

  function _keyPress(e) {
    if (e.keyCode === 13) {
      setNameSearch(e.target.value);
    }
  }

  return (
    <div>
      <Dialog
        fullWidth={true}
        maxWidth={'md'}
        open={open}
        onClose={handleCloseWithCleanup}
        aria-labelledby="team-dialog-title"
        aria-describedby="team-dialog-description"
        className="tug-dialog"
      >
        <MuiDialogTitle className="tug-dialog__title" disableTypography id="team-dialog-title">
          <Typography variant="h6" component="h2">
            {isCreate ? 'Create New Team' : (props.teamData?.title || '')}
          </Typography>
          <IconButton aria-label="close" className="tug-dialog__close" onClick={handleCloseWithCleanup}>
            <CloseIcon style={{ fontSize: 28 }} />
          </IconButton>
        </MuiDialogTitle>

        <div style={{ padding: '20px 24px' }}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            defaultValue={props.teamData?.title || ''}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError('');
            }}
            margin="normal"
            variant="outlined"
            error={!!titleError}
            helperText={titleError}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            defaultValue={props.teamData?.description || ''}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
          />
          
        </div>

        <DialogContent className="tug-dialog__content">
          <SimpleSearch keyPress={_keyPress} />
            <TransferList
              left={left}
              right={right}
              rightTitle={"Team: " + (isCreate ? 'New Team' : props.teamData?.title)}
              leftTitle="Users"
              handleChange={handleChange}
            />
        
        </DialogContent>
         {isLoading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography>Loading...</Typography>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography color="error">Error! {error.message}</Typography>
            </div>
          )}
        {/* TODO: Add search component for users not in team */}
        <div style={{ padding: '20px 24px' }}>
        <FormControl fullWidth variant="outlined" margin="normal" error={!!captainError}>
            <InputLabel id="captain-label">Captain</InputLabel>
            <Select
              labelId="captain-label"
              id="captain-select"
              name="captain"
              value={selectedCaptain?.value || ''}
              onChange={(e) => {
                const selected = captainOptions.find(opt => opt.value === e.target.value);
                setSelectedCaptain(selected || null);
                if (captainError) setCaptainError('');
              }}
              label="Captain"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {captainOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {captainError && <FormHelperText>{captainError}</FormHelperText>}
          </FormControl>
          </div>
        <DialogActions className="tug-dialog__footer">
          <CustomButton
            variant="contained"
            size="medium"
            className="tug-dialog__button"
            onClick={save}
          >
            Save
          </CustomButton>
        </DialogActions>

      </Dialog>
    </div>
  )
}

export default TeamSettings;
